'use client'

import { forwardRef, type AnchorHTMLAttributes, type MouseEvent } from 'react'
import { pushEvent } from '@/lib/analytics/gtag'
import { trackMetaContact } from '@/lib/analytics/meta-pixel'
import {
  EMAIL_CLICKED,
  PHONE_CLICKED,
  type CtaLocation,
} from '@/lib/analytics/events'

type TrackedLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'> & {
  href: string
  location: CtaLocation
  label: string
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

export const TrackedLink = forwardRef<HTMLAnchorElement, TrackedLinkProps>(
  function TrackedLink({ href, location, label, children, onClick, ...rest }, ref) {
    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      // Each channel fires twice by design: once into the dataLayer for GA4 via
      // GTM, once into the Meta Pixel. They are separate reporting systems that
      // never see each other's events — GTM cannot feed Meta here, because GTM
      // runs inside Partytown's worker and the pixel lives on the main thread.
      if (href.startsWith('tel:')) {
        pushEvent(PHONE_CLICKED, { location, label })
        trackMetaContact({ channel: 'phone', location })
      } else if (href.startsWith('mailto:')) {
        pushEvent(EMAIL_CLICKED, { location, label })
        trackMetaContact({ channel: 'email', location })
      }
      onClick?.(event)
    }
    return (
      <a ref={ref} href={href} onClick={handleClick} {...rest}>
        {children}
      </a>
    )
  },
)
